import React           from 'react'
import { useCalendar } from './useCalendar';
import { CALENDAR_VIEWS } from '@helpers/javascript/calendar_helper'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Checkbox            from '../shared/checkbox/checkbox'

const ResourceList = () => {
  const { setState, TOOLS, filter, setFilter, matchesCategoryFilter } = useCalendar()

  const OPEN_ID            = "open"
  const DISPLAY_INTEGRATED = "integrated"

  // Clicking a name is a shortcut on top of the checkboxes:
  //  • stacked "Semaine (vertical)" → scroll straight to that person's planning;
  //  • otherwise → isolate their interventions (toggle "solo").
  const handleNameClick = resource => {
    const inStackedAgenda = filter.view.name === CALENDAR_VIEWS.agenda && filter.stackedAgenda
    if (inStackedAgenda) {
      setState(prevState => ({ ...prevState, scrollToResourceId: resource.id }))
    } else {
      soloResource(resource)
    }
  }

  const soloResource = resource => {
    const allIds     = TOOLS.resources.map(res => res.id)
    // Keep the resource's parent (a technician's manager) visible too:
    // FullCalendar can't render a child row whose parent row is hidden, so
    // isolating a technician without its manager would display nothing.
    const keepIds    = [resource.id, resource.parentId].filter(Boolean)
    const checkedIds = allIds.filter(id => !filter.unchecked.includes(id))
    const isSolo     = checkedIds.length === keepIds.length && keepIds.every(id => checkedIds.includes(id))
    setFilter(prevFilter => ({
      ...prevFilter,
      unchecked: isSolo ? [] : allIds.filter(id => !keepIds.includes(id))
    }))
  }

  const handleResourceChange = (checked, resource) => {
    let unchecked = [...filter.unchecked]
    let resourcesToHandle = TOOLS.resources.filter(res => res.parentId == resource.id || res.id == resource.id)
    resourcesToHandle = resourcesToHandle.map(res => checked ? [res.id, res.parentId] : res.id)
    let result
    if (checked) {
      result = unchecked.filter(item => !resourcesToHandle.flat().includes(item));
    } else {
      result = unchecked.concat(resourcesToHandle.filter(item => !unchecked.includes(item.id)));
    }

    setFilter(prevFilter=>({...prevFilter, unchecked: result}))
  }

  const openWorkorder = TOOLS.resources.find(resource => resource.id == OPEN_ID)
  const managers      = TOOLS.resources.filter(resource => resource.manager == true)

  // ── Material resources filter (group → categories → resources) ──────────
  const resourceGroup      = TOOLS.resources.find(resource => resource.resourceGroup)
  const resourceCategories = TOOLS.resources.filter(resource => resource.resourceCategory)

  const descendantIds = id => {
    const children = TOOLS.resources.filter(res => res.parentId === id)
    return children.reduce((acc, child) => acc.concat(child.id, descendantIds(child.id)), [])
  }

  const handleResourceTreeChange = (checked, node) => {
    const ids = [node.id, ...descendantIds(node.id)]
    setFilter(prevFilter => ({
      ...prevFilter,
      unchecked: checked
        ? prevFilter.unchecked.filter(id => !ids.includes(id))
        : [...new Set([...prevFilter.unchecked, ...ids])]
    }))
  }

  const userInfo = info =>
  <span
    className="mt-1"
    onMouseEnter={e => setState(prevState=>({...prevState, showPopup: {x: e.pageX, y: e.pageY, content: info}}))}
    onMouseLeave={() => setState(prevState=>({...prevState, showPopup: false}))}
  >
    <FontAwesomeIcon icon='info-circle'/>
  </span>

  const renderManager = manager => {
    const allTechnicians = TOOLS.resources.filter(resource => resource.parentId == manager.id)

    if (!matchesCategoryFilter(manager) && allTechnicians.length === 0) return null

    return(
      <React.Fragment key={manager.id}>
        <div className="d-flex calendar-resource-row">
          <Checkbox
            checked    = {!filter.unchecked.includes(manager.id)}
            id         = {manager.id}
            onChange   = {checked => handleResourceChange(checked, manager)}
            label      = ""
            color      = {manager.color}
            className  = "resource-user-checkbox form-group mr-1"
          />
          <span className="resource-name-link" onClick={() => handleNameClick(manager)}>{manager.title}</span>
          {manager.information && userInfo(manager.information)}
        </div>

        { allTechnicians.map(technician =>
            <div className="d-flex calendar-resource-row" key={technician.id}>
              <Checkbox
                className = 'resource-user-checkbox form-group ml-2 mr-1'
                checked   = {!filter.unchecked.includes(technician.id)}
                id        = {technician.id}
                onChange  = {checked => handleResourceChange(checked, technician)}
                label     = ""
                color     = {technician.color}
              />
              <span className="resource-name-link" onClick={() => handleNameClick(technician)}>{technician.title}</span>
              {technician.information && userInfo(technician.information)}
            </div>
        )}
      </React.Fragment>
    )
  }

  return (
    <div className="resource-list">
      <div>
        { TOOLS.displayOpen == DISPLAY_INTEGRATED &&
          <div className="d-flex" key={openWorkorder.id}>
            <Checkbox
              checked    = {!filter.unchecked.includes(openWorkorder.id)}
              id         = {openWorkorder.id}
              onChange   = {checked => handleResourceChange(checked, openWorkorder)}
              label      = {openWorkorder.name}
              color      = {openWorkorder.color}
              className  = "form-group mr-2"
            />
            {openWorkorder.information && userInfo(openWorkorder.information)}
          </div>
        }
        {managers.map(manager => renderManager(manager))}

        { resourceGroup &&
          <div className="resource-section mt-3">
            <div className="d-flex">
              <Checkbox
                checked   = {!filter.unchecked.includes(resourceGroup.id)}
                id        = {resourceGroup.id}
                onChange  = {checked => handleResourceTreeChange(checked, resourceGroup)}
                label     = {resourceGroup.title}
                className = "form-group mr-2 resource-group-label"
              />
            </div>
            { resourceCategories.map(category =>
              <div key={category.id}>
                <div className="d-flex">
                  <Checkbox
                    className = "form-group ml-2 mr-2"
                    checked   = {!filter.unchecked.includes(category.id)}
                    id        = {category.id}
                    onChange  = {checked => handleResourceTreeChange(checked, category)}
                    label     = {category.title}
                    color     = {category.color}
                  />
                </div>
                { TOOLS.resources.filter(res => res.parentId === category.id).map(resource =>
                  <div className="d-flex" key={resource.id}>
                    <Checkbox
                      className = "form-group ml-4 mr-2"
                      checked   = {!filter.unchecked.includes(resource.id)}
                      id        = {resource.id}
                      onChange  = {checked => handleResourceTreeChange(checked, resource)}
                      label     = {resource.title}
                      color     = {resource.color}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        }
      </div>
    </div>
  )
}

export default ResourceList
